import { CURSE_NAMES, HECKLES, heckleText } from "../../../../engine/bench";
import { displayName, playersIn, roundByNo } from "../../../../engine/selectors";
import { BREAK_NAME, hhmm } from "../../../../shared/day";
import type { Config, PlayerId, TournamentEvent, TournamentState } from "../../../../engine/types";
// from the plain .ts, not ./common.tsx, so the tests can load these words
import { clockTime } from "./whistle";

const STATUS_WORDS = { in: "is here", break: "is on a break", left: "has left", expected: "is expected" } as const;

const minutes = (ms: number): string => `${Math.round((ms / 60_000) * 100) / 100} min`;

/** One setting of a config change, in words. Null for the ones nobody needs read out. */
function setting(key: keyof Config, patch: Partial<Config>): string | null {
  switch (key) {
    case "courts":
      return `${patch.courts} courts in use`;
    case "gameMs":
      return `games ${minutes(patch.gameMs ?? 0)}`;
    case "changeoverMs":
      return `changeovers ${minutes(patch.changeoverMs ?? 0)}`;
    case "autoAdvance":
      return `rounds start by themselves ${patch.autoAdvance ? "on" : "off"}`;
    case "lastWhistleAt":
      return patch.lastWhistleAt == null ? "last whistle cleared" : `last whistle ${clockTime(patch.lastWhistleAt)}`;
    case "target":
      return `${patch.target} points to win`;
    case "winBy2":
      return `win by 2 ${patch.winBy2 ? "on" : "off"}`;
    case "minGamesForPodium":
      return `${patch.minGamesForPodium} games for the podium`;
    case "randomEvents":
      return `mini games by themselves ${patch.randomEvents ? "on" : "off"}`;
    case "randomEventChance":
      return `mini game chance ${Math.round((patch.randomEventChance ?? 0) * 100)}%`;
    case "eventTypes":
      return "the mini games changed";
    case "look":
      return "the wall's look changed";
    case "sitsOutFirst": {
      const s = patch.sitsOutFirst;
      if (!s || s.rounds < 1) return "nobody sits out the first rounds";
      return s.rounds === 1 ? `${s.name} sits out round 1` : `${s.name} sits out rounds 1 to ${s.rounds}`;
    }
    default:
      return null;
  }
}

/**
 * One event as a sentence, for the undo bar and for "who did what". Names are the ones the admin
 * sees, read from the state as it stands. No full stop: the caller adds what it needs.
 */
export function sentence(state: TournamentState, e: TournamentEvent): string {
  const name = (id: PlayerId): string => displayName(state.players[id], "admin") || id;
  const mini = (id: string): string => state.config.eventTypes.find((x) => x.id === id)?.name ?? id;
  switch (e.type) {
    case "tournament.created":
      return "The day was set up";
    case "config.updated": {
      const said = (Object.keys(e.patch) as (keyof Config)[]).map((k) => setting(k, e.patch)).filter((x) => x !== null);
      if (!said.length) return "Settings changed";
      const text = said.join(", ");
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
    case "player.added":
      return `${e.name.trim()} added`;
    case "player.renamed":
      return `${name(e.id)} renamed to ${e.name.trim()}`;
    case "player.removed":
      return `${name(e.id)} removed`;
    case "player.status":
      return `${name(e.id)} ${STATUS_WORDS[e.status]}`;
    case "player.profile": {
      if (e.photoWaived) return `${name(e.id)} let in without a picture`;
      if (e.photo === 0) return `${name(e.id)}'s picture cleared`;
      if (e.photo !== undefined) return `${name(e.id)} has a new picture`;
      if (e.nickname === null) return `${name(e.id)}'s nickname cleared`;
      return e.nickname ? `${name(e.id)} is now "${e.nickname}"` : `${name(e.id)}'s profile changed`;
    }
    case "heckle.sent": {
      const text = e.text ?? (e.line !== undefined && e.line < HECKLES.length ? heckleText(e.line, e.court) : null);
      return text ? `${name(e.player)} heckled: "${text}"` : `${name(e.player)} heckled`;
    }
    case "heckle.removed":
      return "A heckle removed from the wall";
    case "bench.muted":
      return e.on ? "Heckles muted" : "Heckles back on";
    case "bench.curses":
      return e.on ? "Curses on" : "Curses off";
    case "minigames.set":
      return e.on ? "Mini games on" : "Mini games off";
    case "curse.thrown":
      // an old phone's curse (no side) did nothing; one with no kind was wrong hand
      return `${name(e.player)} cursed a pair on court ${e.court}` + (e.side ? `: ${CURSE_NAMES[e.kind ?? "wrong-hand"]?.toLowerCase() ?? e.kind}` : "");
    case "curse.lifted":
      return `The curse on court ${e.court} lifted`;
    case "pick.made": {
      const pair = roundByNo(state, e.roundNo)?.games.find((g) => g.court === e.court)?.[e.side];
      return `${name(e.player)} picked ${pair ? pair.map(name).join(" & ") : "a pair"}, round ${e.roundNo} court ${e.court}`;
    }
    case "bet.placed":
      return `${name(e.player)} placed a bet`;
    case "player.dubbed":
      return `${name(e.player)} named ${name(e.target)} "${e.nickname}"`;
    case "player.undubbed":
      return `${name(e.id)}'s given name removed`;
    case "round.drafted":
      return e.lineup ? "A new preview drawn" : "The preview cleared";
    case "round.started":
      return "A round started";
    case "round.finished":
      return "The round finished early";
    case "round.closed":
      return "The changeover skipped";
    case "clock.extended":
      return e.ms < 0 ? `${minutes(-e.ms)} taken off the clock` : `${minutes(e.ms)} added to the clock`;
    case "game.scored":
      return `Round ${e.roundNo} court ${e.court}: ${e.score.a}-${e.score.b}`;
    case "game.corrected":
      return `Round ${e.roundNo} court ${e.court} set to ${e.score.a}-${e.score.b}`;
    case "game.voided":
      return `Round ${e.roundNo} court ${e.court} voided`;
    case "mode.switched":
      return `Switched to ${e.mode === "mexicano" ? "Mexicano" : "Americano"}`;
    case "event.fired":
      // a twist (a handicap) starts at once on every court; a scoring mini game is for the round
      return state.config.eventTypes.find((x) => x.id === e.eventId)?.kind === "handicap"
        ? `${mini(e.eventId)} on every court now, round ${e.roundNo}`
        : `${mini(e.eventId)} set for round ${e.roundNo}`;
    case "twist.stopped":
      return "The twist stopped";
    case "event.cleared":
      return `The mini game taken off round ${e.roundNo}`;
    case "lineup.swapped": {
      // Two on court change places (engine/state.ts). Both are on a court of that round's line-up
      // before the swap and after it, so the undo bar (the state before) and Who did what (the
      // state now) read it the same; a swap with the bench has only one of them on court.
      const lineup = roundByNo(state, e.roundNo)?.lineup ?? (e.roundNo === state.rounds.length + 1 ? state.draft : null);
      const onCourt = new Set(lineup ? playersIn(lineup) : []);
      return onCourt.has(e.out) && onCourt.has(e.in)
        ? `${name(e.out)} and ${name(e.in)} changed places, round ${e.roundNo}`
        : `${name(e.in)} on for ${name(e.out)}, round ${e.roundNo}`;
    }
    case "round.cancelled":
      return "The round taken back";
    case "wall.message":
      return e.text === null ? "The wall message taken down" : `On the wall: "${e.text.trim()}"`;
    case "pause.all":
      if (!e.on) return "Play resumed";
      // a break with a time or a name; sent again while paused it only moves the time or the name
      if (e.until != null) return `${e.label?.trim() || state.pauseLabel || BREAK_NAME}: back at ${hhmm(e.until)}`;
      if (e.label?.trim()) return `Paused for ${e.label.trim()}`;
      return state.pausedAll && e.until === null ? "The time to be back taken off" : "Everything paused";
    case "reveal": {
      const what = e.board === "awards" ? "The awards" : e.board === "main" ? "The main podium" : "The fun board";
      return `${what} ${e.on ? "shown" : "hidden"}`;
    }
    case "winddown.set":
      return e.on ? `Wind down started${e.near ? ", a few to end one game short" : ""}` : "Wind down cancelled";
    case "slides.set":
      return e.slide === null ? "Slides off" : `Slides: slide ${e.slide + 1}`;
    case "reveal.step": {
      if (e.step === null) return "The reveal taken off the wall";
      if (e.step <= e.of) return `Award ${e.step} of ${e.of} handed out`;
      if (e.places < 1) return "Podium shown, with nobody on it yet";
      const place = e.places - (e.step - e.of) + 1;
      return `Podium: ${["", "1st", "2nd", "3rd"][place] ?? "a"} place shown`;
    }
    default:
      return "Something changed";
  }
}

/** Who an event's `by` is, for the log: an admin's name, a court's tablet, or a player. */
export function actor(state: TournamentState, by: string): string {
  if (by.startsWith("admin:")) return by.slice(6) || "Admin";
  if (by === "admin") return "Admin";
  if (by === "projector") return "The wall";
  if (by === "remote") return "The watch";
  const court = /^court-(\d+)$/.exec(by);
  if (court) return `Court ${court[1]}`;
  const player = state.players[by];
  return player ? displayName(player, "admin") : by;
}
